import { motion, AnimatePresence } from "framer-motion";
import { useMemo, useState } from "react";
import {
  FEATURES,
  FEATURE_CATEGORIES,
  type FeatureCategory,
  type FeatureDef,
} from "../features/featuresCatalog";
import { ProfileMenu } from "./ProfileMenu";
import { logout } from "../ocrpro/realApi";
import { Lockup } from "../../components/Lockup";
import type { Conversation } from "./types";

/**
 * Beyond Sure — UNIFIED PREMIUM SIDEBAR (single source of truth).
 *
 * One sidebar mounts on every screen: chat, documents, features, my
 * policies, family, calendar, add policy. There is NO "old dark
 * sidebar" — this is it. Width stays calm (~280px expanded → 56px
 * rail) so it never crowds the page, but holds the entire ~94-feature
 * catalog via collapsible category sections.
 *
 * Rail anatomy, top → bottom:
 *   1. Brand header (B mark + Beyond Sure wordmark) + collapse toggle
 *   2. New chat CTA + Search anchor
 *   3. Primary nav anchors (always visible, route-aware):
 *      Chat · Documents · My policies · Family · Calendar · Add policy
 *   4. "Discover" section — collapsible groups holding all features
 *      grouped by FEATURE_CATEGORIES from the catalog (My Insurance,
 *      Discover, Claims, Smart Tools, New Features, Advanced Tools,
 *      Specialized). Collapsed by default — premium spacing, clean
 *      icons, clear hover/active. Linear/Notion grade.
 *   5. "All features →" link to the full Features page
 *   6. Recent conversations grouped by time (Today / Last 7 days /
 *      Older) — only shown if there are conversations
 *   7. Account footer (avatar + name + email)
 *
 * Width:
 *   - desktop expanded: 280px (calm, Claude-grade)
 *   - desktop collapsed: 56px (icons only, tooltips on hover)
 *   - mobile: full drawer width via MobileDrawer (no collapse toggle)
 *
 * Backwards-compatible: previous props (collapsed, conversations,
 * activeId, onPickConversation, onNewChat, etc.) still work exactly
 * the same. New consumers can pass `activeRoute` with any of the
 * extended route names; the rail highlights the right anchor.
 *
 * Phase 1 ships the structure with mock data. Real wiring lives in
 * the consumer screens — the sidebar stays presentational.
 * // TODO: wire real /api/chat/history (conversations), /api/user/me
 *   (avatar + entitlement) when backend lands.
 */

/* ───────────────────────────────────────────────────────────
 * Public route surface — must be a superset of every place the
 * sidebar can be mounted. Add a new route → add a literal here.
 * ─────────────────────────────────────────────────────────── */
export type AppRoute =
  | "/chat"
  | "/features"
  | "/documents"
  | "/ocr"
  | "/policies"
  | "/family"
  | "/voice";

interface ChatSidebarProps {
  collapsed: boolean;
  onToggleCollapsed: () => void;
  conversations: Conversation[];
  activeId: string | null;
  onPickConversation: (id: string) => void;
  onNewChat: () => void;
  userName?: string;
  userEmail?: string;
  /** Mobile drawer hides the collapse toggle. */
  hideCollapseToggle?: boolean;
  /** Which top-level route the sidebar is being rendered on. */
  activeRoute?: AppRoute;
  /** Primary nav handlers. The sidebar doesn't navigate itself. */
  onPickFeatures?: () => void;
  onPickSearch?: () => void;
  onPickDocuments?: () => void;
  onPickOcrPro?: () => void;
  onPickVoice?: () => void;
  onPickPolicies?: () => void;
  onPickFamily?: () => void;
  /**
   * Called when the user clicks a feature row inside the Discover
   * accordion. The slug is the FeatureDef.slug — the consumer
   * navigates to /features/<slug>. Defaulting this means even
   * screens that don't pass an explicit handler still let the user
   * jump into a feature page via internal navigation.
   */
  onPickFeatureSlug?: (slug: string) => void;
}

export function ChatSidebar({
  collapsed,
  onToggleCollapsed,
  conversations,
  activeId,
  onPickConversation,
  onNewChat,
  userName,
  userEmail,
  hideCollapseToggle = false,
  activeRoute = "/chat",
  onPickFeatures,
  onPickSearch,
  onPickDocuments,
  onPickOcrPro,
  onPickVoice,
  onPickPolicies,
  onPickFamily,
  onPickFeatureSlug,
}: ChatSidebarProps) {
  const groups = groupByTime(conversations);
  const featuresByCategory = useMemo(() => groupCatalog(), []);
  // Track which categories are open. Persist in component state — a
  // future enhancement could persist to localStorage so the user's
  // preference survives navigation.
  const [openCats, setOpenCats] = useState<Record<string, boolean>>({});
  const toggleCat = (cat: string) =>
    setOpenCats((prev) => ({ ...prev, [cat]: !prev[cat] }));

  return (
    <aside
      data-testid="chat-sidebar"
      data-collapsed={collapsed}
      className={[
        "h-full flex flex-col",
        // Faintly cooler than the chat surface so the rail is felt
        // without drawing the eye. Tasteful frosted glass.
        "bg-[rgb(245_243_239_/_0.86)] backdrop-blur-xl",
        "border-r border-[var(--color-ink-08)]",
        "transition-[width] duration-[220ms] ease-out",
        collapsed ? "w-[56px]" : "w-[280px]",
      ].join(" ")}
    >
      {/* ─── Header: brand mark + collapse toggle ─────────────────── */}
      <div
        className={[
          "flex items-center gap-2 px-3 py-3.5",
          "border-b border-[var(--color-ink-08)]",
        ].join(" ")}
      >
        <a
          href="/login"
          className="inline-flex items-center gap-2 shrink-0"
          aria-label="Beyond Sure home"
        >
          {!collapsed ? (
            <Lockup height={24} />
          ) : (
            // Collapsed rail is 56px wide — too narrow for the full lockup
            // (aspect ~4.5:1). No standalone icon-only crop of the mark
            // exists, so fall back to a compact monogram in this state only.
            <span
              className="inline-flex items-center justify-center w-8 h-8 rounded-[10px]"
              style={{
                background:
                  "linear-gradient(135deg, var(--color-brand-deep) 0%, var(--color-brand) 100%)",
                boxShadow: "0 2px 8px -2px rgba(20,75,51,0.4)",
              }}
            >
              <span className="text-[var(--color-paper)] text-[14px] font-extrabold bsure-display">
                B
              </span>
            </span>
          )}
        </a>
        {!hideCollapseToggle ? (
          <button
            type="button"
            onClick={onToggleCollapsed}
            data-testid="sidebar-collapse"
            aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
            className={[
              "ml-auto inline-flex items-center justify-center",
              "h-7 w-7 rounded-[8px]",
              "text-[var(--color-ink-60)] hover:bg-[var(--color-ink-08)] hover:text-[var(--color-ink)]",
              "transition-colors duration-[var(--duration-fast)]",
            ].join(" ")}
          >
            <svg width="14" height="14" viewBox="0 0 16 16" fill="none" aria-hidden>
              <path
                d={collapsed ? "M5 4l4 4-4 4" : "M11 4l-4 4 4 4"}
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
        ) : null}
      </div>

      {/* ─── Scrollable column for everything below the brand ──────── */}
      <div className="flex-1 overflow-y-auto">
        {/* ── New chat + Search ────────────────────────────────────── */}
        <div className="px-2.5 pt-3 pb-1">
          <button
            type="button"
            onClick={onNewChat}
            data-testid="sidebar-new-chat"
            className={[
              "w-full inline-flex items-center gap-2",
              "rounded-[10px] px-2.5 py-2",
              "border border-[var(--color-ink-08)] bg-[var(--color-paper)]",
              "text-[13.5px] font-medium text-[var(--color-ink)]",
              "hover:border-[var(--color-brand-deep)] hover:bg-[var(--color-brand-soft)] hover:text-[var(--color-brand-deep)]",
              "transition-colors duration-[var(--duration-fast)]",
            ].join(" ")}
          >
            <Icon name="plus" />
            {!collapsed ? <span>New chat</span> : null}
          </button>
        </div>

        {/* ── Primary nav anchors (always visible, route-aware) ───── */}
        <nav
          aria-label="Beyond Sure primary navigation"
          className="px-2.5 pt-1.5 flex flex-col gap-0.5"
        >
          {onPickSearch ? (
            <SidebarAnchor
              collapsed={collapsed}
              label="Search"
              testId="sidebar-search"
              onClick={onPickSearch}
              icon={<Icon name="search" />}
            />
          ) : null}
          <SidebarAnchor
            collapsed={collapsed}
            label="Chat"
            testId="sidebar-chat"
            active={activeRoute === "/chat"}
            onClick={onNewChat}
            icon={<Icon name="chat" />}
          />
          {onPickDocuments ? (
            <SidebarAnchor
              collapsed={collapsed}
              label="Documents"
              testId="sidebar-documents"
              active={activeRoute === "/documents"}
              onClick={onPickDocuments}
              icon={<Icon name="doc" />}
            />
          ) : null}
          {onPickOcrPro ? (
            <SidebarAnchor
              collapsed={collapsed}
              label="OCR Pro"
              testId="sidebar-ocrpro"
              active={activeRoute === "/ocr"}
              onClick={onPickOcrPro}
              icon={<Icon name="ocr" />}
              badge="1/10x"
            />
          ) : null}
          {onPickVoice ? (
            <SidebarAnchor
              collapsed={collapsed}
              label="Voice"
              testId="sidebar-voice"
              active={activeRoute === "/voice"}
              onClick={onPickVoice}
              icon={<Icon name="mic" />}
            />
          ) : null}
          {onPickPolicies ? (
            <SidebarAnchor
              collapsed={collapsed}
              label="My policies"
              testId="sidebar-policies"
              active={activeRoute === "/policies"}
              onClick={onPickPolicies}
              icon={<Icon name="shield" />}
            />
          ) : null}
          {onPickFamily ? (
            <SidebarAnchor
              collapsed={collapsed}
              label="Family"
              testId="sidebar-family"
              active={activeRoute === "/family"}
              onClick={onPickFamily}
              icon={<Icon name="team" />}
            />
          ) : null}
        </nav>

        {/* ── Discover — collapsible category groups ──────────────── */}
        {!collapsed ? (
          <div className="px-2.5 pt-4">
            <div className="flex items-center justify-between px-2 mb-1.5">
              <h3 className="bsure-caps text-[10px] tracking-[0.16em] font-semibold text-[var(--color-ink-40)]">
                Discover
              </h3>
              {onPickFeatures ? (
                <button
                  type="button"
                  onClick={onPickFeatures}
                  data-testid="sidebar-all-features"
                  className={[
                    "text-[10.5px] font-medium text-[var(--color-brand-deep)]",
                    "hover:text-[var(--color-ink)] transition-colors duration-[var(--duration-fast)]",
                  ].join(" ")}
                  aria-label="See the full features catalog"
                >
                  All →
                </button>
              ) : null}
            </div>
            <nav
              aria-label="Discover Beyond Sure features"
              className="flex flex-col gap-0.5"
            >
              {featuresByCategory.map((g) => (
                <CategoryAccordion
                  key={g.category}
                  category={g.category}
                  items={g.items}
                  open={!!openCats[g.category]}
                  onToggle={() => toggleCat(g.category)}
                  onPickFeature={
                    onPickFeatureSlug ?? ((slug) => {
                      // Fallback: full hard navigation. The catalog
                      // is always reachable even without a handler.
                      window.location.assign(`${import.meta.env.BASE_URL}features/${slug}`);
                    })
                  }
                />
              ))}
            </nav>
          </div>
        ) : null}

        {/* ── Recent conversations (only when expanded + non-empty) ── */}
        {!collapsed && groups.length > 0 ? (
          <nav
            aria-label="Recent conversations"
            className="px-2.5 pt-4 pb-2"
          >
            <h3 className="bsure-caps text-[10px] tracking-[0.16em] font-semibold text-[var(--color-ink-40)] px-2 mb-1.5">
              Recent
            </h3>
            {groups.map((g) => (
              <div key={g.label} className="mb-3">
                <h4 className="text-[10.5px] font-medium text-[var(--color-ink-40)] px-2 mb-1">
                  {g.label}
                </h4>
                <ul className="flex flex-col gap-0.5">
                  {g.items.map((c) => {
                    const active = c.id === activeId;
                    return (
                      <li key={c.id}>
                        <button
                          type="button"
                          onClick={() => onPickConversation(c.id)}
                          data-testid={`sidebar-conv-${c.id}`}
                          className={[
                            "w-full text-left rounded-[8px] px-2.5 py-1.5",
                            "text-[13px] leading-[1.4] truncate",
                            active
                              ? "bg-[var(--color-brand-soft)] text-[var(--color-brand-deep)] font-medium"
                              : "text-[var(--color-ink-60)] hover:bg-[var(--color-ink-08)] hover:text-[var(--color-ink)]",
                            "transition-colors duration-[var(--duration-fast)]",
                          ].join(" ")}
                        >
                          {c.title}
                        </button>
                      </li>
                    );
                  })}
                </ul>
              </div>
            ))}
          </nav>
        ) : null}
      </div>

      {/* ─── Account footer ─────────────────────────────────────── */}
      <div
        className={[
          "border-t border-[var(--color-ink-08)] px-2.5 py-2.5",
        ].join(" ")}
      >
        <ProfileMenu
          userName={userName || "You"}
          userEmail={userEmail}
          planName="Enterprise"
          isEnterprise
          messagesUsed={37}
          messagesLimit={150}
          onLogout={() => {
            // AE4.2: invalidate the server token + clear all local token keys,
            // then bounce to /login — no silent half-logged-out state (AE0 finding).
            void logout().finally(() => {
              window.location.href = `${import.meta.env.BASE_URL}login`;
            });
          }}
        >
          {({ onClick, ref, ...aria }) => (
            <button
              ref={ref}
              type="button"
              onClick={onClick}
              aria-label="Account and settings"
              data-testid="sidebar-account"
              {...aria}
              className={[
                "w-full inline-flex items-center gap-2.5",
                "rounded-[8px] px-2 py-1.5",
                "hover:bg-[var(--color-ink-08)]",
                "transition-colors duration-[var(--duration-fast)]",
              ].join(" ")}
            >
              <span
                className="inline-flex items-center justify-center w-7 h-7 shrink-0 rounded-full bg-[var(--color-brand-deep)] text-[var(--color-paper)] text-[12px] font-semibold"
              >
                {(userName || userEmail || "?").trim().charAt(0).toUpperCase()}
              </span>
              {!collapsed ? (
                <span className="flex-1 min-w-0 text-left">
                  <span className="block text-[13px] font-medium text-[var(--color-ink)] truncate">
                    {userName || "You"}
                  </span>
                  {userEmail ? (
                    <span className="block text-[11px] text-[var(--color-ink-40)] truncate">
                      {userEmail}
                    </span>
                  ) : null}
                </span>
              ) : null}
            </button>
          )}
        </ProfileMenu>
      </div>
    </aside>
  );
}

/* ───────────────────────────────────────────────────────────
 * MobileDrawer — slide-over wrapper for the sidebar on phones
 * ─────────────────────────────────────────────────────────── */

interface MobileDrawerProps {
  open: boolean;
  onClose: () => void;
  children: React.ReactNode;
}

export function MobileDrawer({ open, onClose, children }: MobileDrawerProps) {
  return (
    <>
      <motion.div
        initial={false}
        animate={{ opacity: open ? 1 : 0 }}
        transition={{ duration: 0.18 }}
        style={{ pointerEvents: open ? "auto" : "none" }}
        onClick={onClose}
        aria-hidden
        className="fixed inset-0 z-[60] bg-[rgb(20_28_24_/_0.32)] backdrop-blur-[2px] sm:hidden"
      />
      <motion.div
        initial={false}
        animate={{ x: open ? 0 : "-100%" }}
        transition={{ duration: 0.24, ease: [0.32, 0.72, 0, 1] }}
        className={[
          "fixed top-0 left-0 z-[61] h-[100dvh] w-[86%] max-w-[320px]",
          "sm:hidden",
          "shadow-[6px_0_28px_-12px_rgba(20,75,51,0.4)]",
        ].join(" ")}
        role="dialog"
        aria-modal="true"
        aria-label="Beyond Sure navigation"
        data-testid="mobile-drawer"
      >
        {children}
      </motion.div>
    </>
  );
}

/* ───────────────────────────────────────────────────────────
 * SidebarAnchor — primary nav row (icon + label)
 * ─────────────────────────────────────────────────────────── */

interface SidebarAnchorProps {
  collapsed: boolean;
  label: string;
  testId: string;
  onClick: () => void;
  icon: React.ReactNode;
  active?: boolean;
  /** Optional badge on the right edge — used for OCR Pro's "1/10x" chip. */
  badge?: string;
}

function SidebarAnchor({
  collapsed,
  label,
  testId,
  onClick,
  icon,
  active = false,
  badge,
}: SidebarAnchorProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      aria-current={active ? "page" : undefined}
      title={collapsed ? label : undefined}
      className={[
        "w-full inline-flex items-center gap-2",
        "rounded-[10px] px-2.5 py-1.5",
        "text-[13px] font-medium",
        active
          ? "bg-[var(--color-brand-soft)] text-[var(--color-brand-deep)]"
          : "text-[var(--color-ink-60)] hover:bg-[var(--color-ink-08)] hover:text-[var(--color-ink)]",
        "transition-colors duration-[var(--duration-fast)]",
      ].join(" ")}
    >
      <span className="shrink-0 inline-flex items-center justify-center w-[18px] h-[18px]">
        {icon}
      </span>
      {!collapsed ? <span className="truncate flex-1 text-left">{label}</span> : null}
      {!collapsed && badge ? (
        <span
          className={[
            "shrink-0 inline-flex items-center h-4 px-1.5 rounded-full",
            "text-[9px] font-semibold uppercase tracking-[0.10em] tabular-nums",
            active
              ? "bg-[var(--color-brand-deep)] text-[var(--color-paper)]"
              : "bg-[var(--color-brand-soft)] text-[var(--color-brand-deep)]",
          ].join(" ")}
        >
          {badge}
        </span>
      ) : null}
    </button>
  );
}

/* ───────────────────────────────────────────────────────────
 * CategoryAccordion — collapsible Discover group with feature rows
 * ─────────────────────────────────────────────────────────── */

interface CategoryAccordionProps {
  category: FeatureCategory;
  items: FeatureDef[];
  open: boolean;
  onToggle: () => void;
  onPickFeature: (slug: string) => void;
}

function CategoryAccordion({
  category,
  items,
  open,
  onToggle,
  onPickFeature,
}: CategoryAccordionProps) {
  return (
    <div data-testid={`sidebar-cat-${slugify(category)}`}>
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={open}
        className={[
          "w-full inline-flex items-center gap-2",
          "rounded-[10px] px-2.5 py-1.5",
          "text-[13px] font-medium",
          "text-[var(--color-ink-60)] hover:bg-[var(--color-ink-08)] hover:text-[var(--color-ink)]",
          "transition-colors duration-[var(--duration-fast)]",
        ].join(" ")}
      >
        <span
          className={[
            "shrink-0 inline-flex items-center justify-center w-[16px] h-[16px]",
            "transition-transform duration-[var(--duration-fast)]",
            open ? "rotate-90" : "rotate-0",
          ].join(" ")}
          aria-hidden
        >
          <svg width="10" height="10" viewBox="0 0 16 16" fill="none">
            <path
              d="M6 4l4 4-4 4"
              stroke="currentColor"
              strokeWidth="1.8"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </span>
        <span className="truncate">{category}</span>
        <span className="ml-auto text-[10.5px] tabular-nums text-[var(--color-ink-40)]">
          {items.length}
        </span>
      </button>
      <AnimatePresence initial={false}>
        {open ? (
          <motion.ul
            key="items"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.22, ease: [0.32, 0.72, 0, 1] }}
            className="overflow-hidden"
          >
            <div className="flex flex-col gap-0.5 pl-6 pr-1 py-1">
              {items.map((feat) => (
                <li key={feat.slug}>
                  <button
                    type="button"
                    onClick={() => onPickFeature(feat.slug)}
                    data-testid={`sidebar-feat-${feat.slug}`}
                    title={feat.hint}
                    className={[
                      "w-full text-left rounded-[8px] px-2.5 py-1",
                      "text-[12.5px] leading-[1.45] truncate",
                      "text-[var(--color-ink-60)] hover:bg-[var(--color-ink-08)] hover:text-[var(--color-ink)]",
                      "transition-colors duration-[var(--duration-fast)]",
                    ].join(" ")}
                  >
                    {feat.name}
                    {feat.isNew ? (
                      <span
                        className={[
                          "ml-1.5 inline-block align-middle",
                          "h-1.5 w-1.5 rounded-full",
                          "bg-[var(--color-brand-deep)]",
                        ].join(" ")}
                        aria-label="New"
                      />
                    ) : null}
                  </button>
                </li>
              ))}
            </div>
          </motion.ul>
        ) : null}
      </AnimatePresence>
    </div>
  );
}

/* ───────────────────────────────────────────────────────────
 * Helpers
 * ─────────────────────────────────────────────────────────── */

interface ConvGroup {
  label: string;
  items: Conversation[];
}

function groupByTime(conversations: Conversation[]): ConvGroup[] {
  const now = Date.now();
  const day = 24 * 60 * 60 * 1000;
  const groups: Record<string, Conversation[]> = {
    Today: [],
    "Last 7 days": [],
    Older: [],
  };
  for (const c of conversations) {
    const age = now - c.lastActiveAt;
    if (age < day) groups["Today"].push(c);
    else if (age < 7 * day) groups["Last 7 days"].push(c);
    else groups["Older"].push(c);
  }
  const labels = ["Today", "Last 7 days", "Older"];
  return labels
    .map((label) => ({
      label,
      items: groups[label].sort((a, b) => b.lastActiveAt - a.lastActiveAt),
    }))
    .filter((g) => g.items.length > 0);
}

function groupCatalog(): { category: FeatureCategory; items: FeatureDef[] }[] {
  return FEATURE_CATEGORIES.map((category) => ({
    category,
    items: FEATURES.filter((f) => f.category === category),
  })).filter((g) => g.items.length > 0);
}

function slugify(s: string): string {
  return s.toLowerCase().replace(/\s+/g, "-");
}

/* ───────────────────────────────────────────────────────────
 * Icon — internal SVG set used by anchor rows only.
 * Keeps the sidebar self-contained (no shared icon module).
 * ─────────────────────────────────────────────────────────── */

type IconName =
  | "plus"
  | "search"
  | "chat"
  | "doc"
  | "shield"
  | "team"
  | "ocr"
  | "mic";

function Icon({ name }: { name: IconName }) {
  switch (name) {
    case "plus":
      return (
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden>
          <path
            d="M12 5v14M5 12h14"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
          />
        </svg>
      );
    case "search":
      return (
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden>
          <circle cx="11" cy="11" r="6" stroke="currentColor" strokeWidth="1.8" />
          <path
            d="M20 20l-4-4"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinecap="round"
          />
        </svg>
      );
    case "chat":
      return (
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden>
          <path
            d="M21 12a8 8 0 1 1-3.5-6.6L21 4l-1.4 3.4A8 8 0 0 1 21 12z"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinejoin="round"
            strokeLinecap="round"
          />
        </svg>
      );
    case "doc":
      return (
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden>
          <path
            d="M7 3h8l4 4v13a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2z"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinejoin="round"
          />
          <path
            d="M14 3v5h5M9 13h6M9 17h4"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      );
    case "shield":
      return (
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden>
          <path
            d="M12 3l8 3v6c0 5-3.4 8.4-8 9-4.6-.6-8-4-8-9V6l8-3z"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinejoin="round"
          />
        </svg>
      );
    case "team":
      return (
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden>
          <circle cx="9" cy="9" r="3.2" stroke="currentColor" strokeWidth="1.8" />
          <path
            d="M3 19.5c1-3 3.5-4.5 6-4.5s5 1.5 6 4.5"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinecap="round"
          />
          <circle cx="17" cy="8" r="2.4" stroke="currentColor" strokeWidth="1.6" />
          <path
            d="M14.5 13.5c.8-.3 1.6-.5 2.5-.5 1.8 0 3.3.8 4.2 2.4"
            stroke="currentColor"
            strokeWidth="1.6"
            strokeLinecap="round"
          />
        </svg>
      );
    case "ocr":
      // Sparkle-on-a-page glyph — signals "AI extraction" without
      // looking gimmicky. Brand-aligned.
      return (
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden>
          <path
            d="M7 3h8l4 4v13a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2z"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinejoin="round"
          />
          <path
            d="M14 3v5h5"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M12.5 11.5l.6 1.4 1.4.6-1.4.6-.6 1.4-.6-1.4-1.4-.6 1.4-.6.6-1.4z"
            stroke="currentColor"
            strokeWidth="1.4"
            strokeLinejoin="round"
            fill="currentColor"
            fillOpacity="0.18"
          />
        </svg>
      );
    case "mic":
      return (
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden>
          <rect
            x="9"
            y="3"
            width="6"
            height="11"
            rx="3"
            stroke="currentColor"
            strokeWidth="1.8"
          />
          <path
            d="M5 11a7 7 0 0 0 14 0M12 18v3"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinecap="round"
          />
        </svg>
      );
  }
}
